import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_REGISTRY } from './RouteRegistry.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useMenu } from '../../../shared/hooks/useMenu.js';

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps): React.ReactElement | null {
  const [q, setQ] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const { token } = useAuth();
  // Search what the admin actually granted, not the static fallback: the DB menu
  // carries whole sections (e.g. Platform Operations) that ROUTE_REGISTRY omits.
  const { menu } = useMenu('/api', token, 'web');

  const entries = React.useMemo(() => {
    const byPath = new Map<string, { path: string; label: string }>();
    for (const section of menu?.sections ?? []) {
      for (const item of section.items) {
        if (item.path) byPath.set(item.path, { path: item.path, label: item.label });
      }
    }
    if (byPath.size > 0) return Array.from(byPath.values());
    return ROUTE_REGISTRY.map((r) => ({ path: r.path, label: r.label }));
  }, [menu]);

  React.useEffect(() => {
    if (open) {
      setQ('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  const filtered = entries
    .filter(
      (r) =>
        r.label.toLowerCase().includes(q.toLowerCase()) ||
        r.path.toLowerCase().includes(q.toLowerCase()),
    )
    .slice(0, 20);

  // Typing shortens the list, so an index kept from the longer one would point
  // past the end and leave nothing highlighted.
  const active = filtered.length === 0 ? -1 : Math.min(activeIndex, filtered.length - 1);

  function move(delta: number): void {
    if (filtered.length === 0) return;
    const next = (active + delta + filtered.length) % filtered.length;
    setActiveIndex(next);
    listRef.current
      ?.querySelectorAll('[role="option"]')
      [next]?.scrollIntoView({ block: 'nearest' });
  }

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); return; }
    if (e.key === 'Home') { e.preventDefault(); setActiveIndex(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActiveIndex(filtered.length - 1); return; }
    if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      navigate(filtered[active].path);
      onClose();
    }
  }

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-bg-overlay)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 80,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="command-palette"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-elevated)',
          width: 520,
          maxHeight: '70vh',
          borderRadius: 8,
          padding: 12,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <input
          ref={inputRef}
          type="search"
          placeholder="Search pages…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setActiveIndex(0); }}
          onKeyDown={onKeyDown}
          aria-label="Search commands"
          aria-controls="command-palette-list"
          aria-activedescendant={active >= 0 ? `cp-opt-${active}` : undefined}
          style={{
            width: '100%',
            padding: 8,
            border: '1px solid var(--color-border-default)',
            borderRadius: 4,
            marginBottom: 8,
            background: 'var(--color-bg-canvas)',
          }}
        />
        <ul ref={listRef} id="command-palette-list" role="listbox" style={{ listStyle: 'none', padding: 0, margin: 0, overflowY: 'auto' }}>
          {filtered.map((r, i) => (
            <li key={r.path}>
              <button
                type="button"
                role="option"
                id={`cp-opt-${i}`}
                aria-selected={i === active}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => {
                  navigate(r.path);
                  onClose();
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: 4,
                  background: i === active ? 'var(--color-bg-selected)' : 'transparent',
                  outline: i === active ? '2px solid var(--color-border-focus)' : 'none',
                  outlineOffset: -2,
                  fontWeight: i === active ? 600 : 400,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--color-text-primary)',
                }}
              >
                <span>{r.label}</span>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                  {r.path}
                </span>
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li style={{ padding: 12, color: 'var(--color-text-muted)' }}>No matches</li>
          )}
        </ul>
      </div>
    </div>
  );
}
